/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/indent */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { AddComment, Ticket, TicketComment, TicketCountByStatus, UploadResponse } from "./ticket.type";

@Injectable({
  providedIn: "root",
})
export class TicketService {
  basePath = `${environment.serverUrl}/tickets`;
  zendeskBasePath = `${environment.zendeskApi}`;
  basePathCsvQueue = `${environment.serverUrl}/tickets/queue-csv`;

  constructor(private http: HttpClient) {}

  createTicket(data: Ticket): Observable<Ticket> {
    return this.http.post<Ticket>(this.basePath, data).pipe(
      tap((_data: Ticket) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getTickets(options?: QueryParams<Ticket>): Observable<PaginatedResponse<Ticket>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<Ticket>>(this.basePath, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getTicket(ticketId: string): Observable<Ticket> {
    let params: HttpParams = new HttpParams();
    params = params.set("select", JSON.stringify({ asset: true }));
    params = params.set("relations", JSON.stringify({ asset: { company: true }, contract: true }));
    return this.http.get<Ticket>(`${this.basePath}/${ticketId}`, { params }).pipe(
      tap((_data: Ticket) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getTicketCountByStatus(options?: QueryParams<Ticket>): Observable<TicketCountByStatus> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<TicketCountByStatus>(`${this.basePath}/count-by-status`, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateTicket(ticketId: string, data): Observable<Ticket> {
    return this.http.put<Ticket>(`${this.basePath}/${ticketId}`, data).pipe(
      tap((_data: Ticket) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getTicketComments(
    ticketId: string,
    options?: QueryParams<TicketComment>,
  ): Observable<PaginatedResponse<TicketComment>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<TicketComment[]>(`${this.basePath}/${ticketId}/comments`, { params }).pipe(
      tap((_data: TicketComment[]) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  uploadFile(file: File): Observable<UploadResponse> {
    return this.http
      .post<UploadResponse>(`${this.zendeskBasePath}/uploads?filename=${file.name}`, file, {
        headers: {
          "content-type": "application/binary",
          "x-skip-auth": "1",
        },
      })
      .pipe(
        tap((_data: UploadResponse) => console.log()),
        catchError(this.handleError<any>("")),
      );
  }

  addComment(ticketId: string, comment: AddComment): Observable<TicketComment[]> {
    return this.http.post<Ticket>(`${this.basePath}/${ticketId}/comments`, comment).pipe(
      tap((_data: Ticket) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getTicketsQueue(options?: QueryParams<Ticket>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error || "Something went wrong");
    };
  }
}
